import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { setTenantScope } from './useDataCache';

interface User {
  id: number;
  fullName: string;
  email: string;
  emailVerified: boolean;
  permissions?: string[];
  tenantId?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string, tenantSlug: string) => Promise<void>;
  logout: () => Promise<void>;
  signup: (data: SignupData) => Promise<void>;
}

interface SignupData {
  empresa: string;
  nombreCompleto: string;
  email: string;
  usuario: string;
  contraseña: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already authenticated
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.user) {
          setUser(result.user);
          // CRITICAL: Set tenant scope for data cache after successful refresh
          setTenantScope(result.user.tenantId);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string, tenantSlug: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/auth/login/${tenantSlug}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }

      const result = await response.json();
      setUser(result.user);
      // CRITICAL: Set tenant scope after successful login to prevent cross-tenant data
      if (result.user && result.user.tenantId) {
        setTenantScope(result.user.tenantId);
      }
      
      // Store tenant slug for logout redirection
      localStorage.setItem('tenantSlug', tenantSlug);
      
      navigate('/app');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Get tenant slug from localStorage (saved during login)
      const tenantSlug = localStorage.getItem('tenantSlug') || 'demo-corp';
      
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      
      // CRITICAL: Clear tenant scope and cache on logout to prevent cross-tenant data exposure
      setTenantScope(null);
      setUser(null);
      
      // NOTE: We keep tenantSlug in localStorage so user returns to their tenant's login page
      
      // Redirect to the tenant's login page
      navigate(`/${tenantSlug}/login`);
    } catch (error) {
      console.error('Logout error:', error);
      // CRITICAL: Clear tenant scope and cache even on error
      setTenantScope(null);
      setUser(null);
      
      // Get tenant slug even on error
      const tenantSlug = localStorage.getItem('tenantSlug') || 'demo-corp';
      
      // Redirect to tenant's login page
      navigate(`/${tenantSlug}/login`);
    }
  };

  const signup = async (data: SignupData) => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/tenants/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Signup failed');
      }

      const result = await response.json();
      setUser(result.user);
      navigate('/app');
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    isLoading,
    login,
    logout,
    signup,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}